import React from 'react';
import type { SceneAlternatesState } from './useSceneAlternates';

/**
 * ‹ › paging over a scene's takes, laid over the media the panel already
 * shows — the poster (or standalone still) IS the current take, so the arrows
 * make it browsable in place instead of sending the eye down to the thumbnail
 * strip. Styled to match GenerationCarousel's arrows so the two take-paging
 * surfaces of the app read as one control.
 *
 * Unlike the carousel, an arrow here PICKS the neighbouring take rather than
 * paging a local view: the media underneath always renders the BOUND take, so
 * a view-only cursor would desync from the picture within one click. That
 * makes every press a real rebind — hence disabled while one is in flight —
 * and wrap-around means the last take's ›  is a step back to the first, never
 * a dead end. Picks route through the same hook the strip uses, so the strip's
 * highlight moves with the arrows for free.
 *
 * Renders inside a `position: relative` container as a SIBLING of whatever
 * shows the media — never inside a button (the poster's play button is one),
 * where nested interactive elements are invalid and every arrow press would
 * also toggle playback.
 */

export interface AlternatesArrowsOverlayProps {
  /** Hoisted takes state — the same instance the thumbnail strip renders. */
  state: SceneAlternatesState;
  /** Names the medium in accessible labels, e.g. "video take". */
  label?: string;
}

const ARROW_CLASS =
  'absolute top-1/2 z-10 -translate-y-1/2 bg-black/60 px-1 py-2 text-[10px] text-white ' +
  'hover:bg-black/80 disabled:opacity-40';

const AlternatesArrowsOverlay: React.FC<AlternatesArrowsOverlayProps> = ({
  state,
  label = 'take',
}) => {
  const { takes, pending, pick } = state;

  // One take is nothing to page through — the overlay disappears entirely, so
  // a single-shot scene looks exactly as it did before the arrows existed.
  if (takes.length < 2) return null;

  // The hook guarantees exactly one row marked current (implicit head when the
  // backend cannot name one); the max() is belt-and-braces for a transient
  // refetch gap, landing on the newest rather than off the end.
  const currentIndex = Math.max(
    takes.findIndex((take) => take.current),
    0
  );

  const step = (delta: number) => {
    const next = takes[(currentIndex + delta + takes.length) % takes.length];
    void pick(next);
  };

  return (
    <>
      <button
        type="button"
        className={`${ARROW_CLASS} left-0`}
        disabled={Boolean(pending)}
        onClick={() => step(-1)}
        aria-label={`Previous ${label}`}
        data-testid="alternates-arrow-prev"
      >
        ‹
      </button>
      <button
        type="button"
        className={`${ARROW_CLASS} right-0`}
        disabled={Boolean(pending)}
        onClick={() => step(1)}
        aria-label={`Next ${label}`}
        data-testid="alternates-arrow-next"
      >
        ›
      </button>
      <span
        className="absolute bottom-0 right-0 z-10 bg-black/70 px-1 text-[9px] text-white"
        data-testid="alternates-take-counter"
      >
        {currentIndex + 1}/{takes.length}
      </span>
    </>
  );
};

export default AlternatesArrowsOverlay;
